import React, { useState, useEffect, useCallback, useRef } from 'react'
import Header from '../Component/Header';
import Footer from '../Component/footer';
import PageHead from '../Component/pageHead'
import { Form } from 'react-bootstrap'
import { PostAction } from '../Component/PostAction';
import { useFormik } from 'formik';
import { GetAction } from '../Component/GetAction'
import { Link, useHistory } from 'react-router-dom'
import {
  consumeDonationResumeCheckout,
  hasDonorSession,
  guestCheckoutPath,
} from '../utils/sessionAuth'
import {
  clearDonationDraft,
  loadDonationDraft,
  saveDonationDraft,
} from '../utils/donationDraft'

const sessionVal = (key, fallback = '') =>
  sessionStorage.getItem(key) ? sessionStorage.getItem(key) : fallback

const buildInitialFormData = () => ({
  id: sessionVal('id'),
  firstName: sessionVal('first_name'),
  lastName: sessionVal('last_name'),
  email: sessionVal('email_address'),
  mobile: sessionVal('mobile'),
  panNumber: sessionVal('pan_number'),
  address1: sessionVal('address1'),
  product_id: '',
  product_name: '',
  same_address: '',
  save_info: '',
  donation_amount: '',
  donation_amount_info: '',
})

function isLoggedIn() {
  return hasDonorSession()
}

function formatAmount(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return `₹ ${n.toLocaleString('en-IN')}`
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve()
      return
    }
    const existing = document.querySelector('script[data-razorpay-checkout]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Razorpay script failed to load.')))
      return
    }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    script.dataset.razorpayCheckout = '1'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Razorpay script failed to load.'))
    document.body.appendChild(script)
  })
}

async function openRazorpayCheckout(checkout, { onSuccess, onDismiss } = {}) {
  if (!checkout?.key || !checkout?.order_id) {
    throw new Error('Invalid payment options from server.')
  }
  await loadRazorpayScript()
  if (!window.Razorpay) {
    throw new Error('Razorpay is not available in this browser.')
  }

  return new Promise((resolve, reject) => {
    const options = {
      key: checkout.key,
      amount: checkout.amount,
      currency: checkout.currency || 'INR',
      name: checkout.name || 'Arihant Trust',
      description: checkout.description || 'Donation',
      order_id: checkout.order_id,
      prefill: checkout.prefill || {},
      notes: checkout.notes || {},
      theme: checkout.theme || { color: '#800000' },
      handler: async (response) => {
        try {
          if (onSuccess) await onSuccess(response)
          resolve(response)
        } catch (err) {
          reject(err)
        }
      },
      modal: {
        ondismiss: () => {
          if (onDismiss) onDismiss()
          resolve(null)
        },
      },
    }
    const rzp = new window.Razorpay(options)
    rzp.on('payment.failed', (response) => {
      const msg =
        response?.error?.description ||
        response?.error?.reason ||
        'Payment failed. Please try again.'
      reject(new Error(msg))
    })
    rzp.open()
  })
}

function Donation() {
  const history = useHistory()
  const loggedIn = isLoggedIn()
  const [getDetails, setDetails] = useState([])
  const [formData] = useState(buildInitialFormData)
  const [pendingDonations, setPendingDonations] = useState([])
  const [pendingLoading, setPendingLoading] = useState(false)
  const [payingId, setPayingId] = useState(null)
  const [payMessage, setPayMessage] = useState('')
  const postUrl = '/donation/checkout'

  const requestAPI = async () => {
    const url = `/donation_type/donationTypes`
    const res = await GetAction('get', { page_id: 1 }, url)
    setDetails(res?.response || [])
  }

  useEffect(() => {
    requestAPI()
  }, [])

  const loadPendingDonations = useCallback(async () => {
    const userId = sessionStorage.getItem('id')
    if (!userId) {
      setPendingDonations([])
      return
    }
    setPendingLoading(true)
    const res = await GetAction(
      'get',
      { user_id: userId, status: 'unpaid' },
      'donation/DonationHistory'
    )
    const rows = Array.isArray(res?.response) ? res.response : []
    setPendingDonations(rows)
    setPendingLoading(false)
  }, [])

  useEffect(() => {
    if (loggedIn) {
      loadPendingDonations()
    }
  }, [loggedIn, loadPendingDonations])

  useEffect(() => {
    if (!loggedIn) return
    const params = new URLSearchParams(window.location.search || '')
    const payment = params.get('payment')
    if (!payment) return
    if (window.history?.replaceState) {
      window.history.replaceState({}, '', window.location.pathname)
    }
    if (payment === 'success') {
      history.replace('/DonationHistory')
      return
    }
    setPayMessage('Payment was not completed. You can try again from this page.')
    loadPendingDonations()
  }, [loggedIn, loadPendingDonations, history])

  const verifyAndFinish = async (checkout, razorpayResponse) => {
    const userId = sessionStorage.getItem('id')
    const detailId = checkout.payment_detail_id
    const res = await PostAction(
      'post',
      {
        user_id: Number(userId),
        payment_detail_id: Number(detailId),
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
      },
      'donation/verifyPayment'
    )
    if (res?.status !== 'success') {
      throw new Error(res?.message || 'Payment could not be verified.')
    }
    clearDonationDraft()
    history.push('/DonationHistory')
  }

  const startRazorpay = async (checkout, busyKey) => {
    setPayingId(busyKey)
    setPayMessage('')
    try {
      const result = await openRazorpayCheckout(checkout, {
        onSuccess: (response) => verifyAndFinish(checkout, response),
        onDismiss: () => {
          setPayMessage('Payment was not completed. You can try again from this page.')
          loadPendingDonations()
        },
      })
      if (result == null) {
        // dismissed / failed — stay on Donation page
      }
    } catch (err) {
      setPayMessage(err?.message || 'Payment failed. You can try again from this page.')
      loadPendingDonations()
    } finally {
      setPayingId(null)
    }
  }

  const payPendingDonation = async (row) => {
    const userId = sessionStorage.getItem('id')
    if (!userId) {
      alert('Please login to pay.')
      return
    }
    const detailId = row.payment_detail_id
    const paymentId = row.payment_id
    if (!detailId && !paymentId) {
      alert('Missing donation reference.')
      return
    }
    const busyKey = detailId || paymentId
    setPayingId(busyKey)
    setPayMessage('')
    const res = await PostAction(
      'post',
      {
        user_id: Number(userId),
        payment_detail_id: detailId ? Number(detailId) : undefined,
        payment_id: paymentId ? Number(paymentId) : undefined,
      },
      'donation/initiatePayment'
    )
    if (res?.status === 'success' && res.response?.order_id && res.response?.key) {
      await startRazorpay(res.response, busyKey)
      return
    }
    setPayingId(null)
    alert(res?.message || 'Unable to start payment.')
  }

  const beginCheckout = async (submitValues) => {
    if (!submitValues.product_id) {
      alert('Please select a donation type.')
      return
    }
    const amount = Number(submitValues.donation_amount)
    if (!amount || amount <= 0) {
      alert('Please enter a valid donation amount.')
      return
    }

    const payload = {
      ...submitValues,
      id: sessionVal('id'),
      firstName: sessionVal('first_name'),
      lastName: sessionVal('last_name'),
      email: sessionVal('email_address'),
      mobile: sessionVal('mobile'),
      panNumber: sessionVal('pan_number'),
      address1: sessionVal('address1'),
      product_id: submitValues.product_id,
      donation_amount: amount,
    }

    setPayingId('checkout')
    setPayMessage('')
    const res = await PostAction('post', payload, postUrl)
    if (res?.status === 'success' && res.response?.order_id && res.response?.key) {
      await startRazorpay(res.response, 'checkout')
      return
    }
    setPayingId(null)
    if (res?.status === 'success') {
      clearDonationDraft()
      loadPendingDonations()
      setPayMessage(
        res?.message ||
          'Donation saved as pending. Complete payment from the list below once Razorpay is configured.'
      )
      return
    }
    alert(res?.message || 'Unable to start checkout.')
    loadPendingDonations()
  }

  const beginCheckoutRef = useRef(beginCheckout)
  beginCheckoutRef.current = beginCheckout
  const resumeStartedRef = useRef(false)

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: formData,
    onSubmit: (submitValues) => {
      if (!isLoggedIn()) {
        saveDonationDraft(submitValues)
        history.push(guestCheckoutPath())
        return
      }
      beginCheckout(submitValues)
    },
  })

  useEffect(() => {
    const draft = loadDonationDraft()
    if (!draft) return
    if (draft.product_id) setFieldValue('product_id', draft.product_id)
    if (draft.product_name) setFieldValue('product_name', draft.product_name)
    if (draft.donation_amount != null && draft.donation_amount !== '') {
      setFieldValue('donation_amount', draft.donation_amount)
    }
    if (draft.donation_amount_info != null) {
      setFieldValue('donation_amount_info', draft.donation_amount_info)
    }
  }, [setFieldValue])

  useEffect(() => {
    if (!loggedIn || resumeStartedRef.current) return
    if (!consumeDonationResumeCheckout()) return
    const draft = loadDonationDraft()
    if (!draft?.product_id || !draft?.donation_amount) return
    resumeStartedRef.current = true
    beginCheckoutRef.current({
      ...formData,
      ...draft,
      id: sessionVal('id'),
    })
  }, [loggedIn, formData])

  return (
    <>
      <Header />
      <PageHead title="ऑनलाइन दान" />
      <section className="donation-page">
        <div className="container">
          <div className="donation-card">
            <div className="donation-card-header">
              <h2 className="donation-card-title">ऑनलाइन दान</h2>
              <p className="donation-card-subtitle">
                आप हमें आपकी दान राशी यहाँ से भी सीधे भेज सकते है। ऑनलाइन दान से संबधी किसी भी प्रकार की
                जानकारी के लिए संपर्क करे ०८९८३ ७८३ ७८३।
              </p>
            </div>

            <Form onSubmit={handleSubmit} className="donation-form">
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="product_id">
                    दान प्रकार / Donation type
                  </label>
                  <select
                    className="form-control"
                    id="product_id"
                    name="product_id"
                    value={values.product_id}
                    onChange={(e) => {
                      const amount = e.target[e.target.selectedIndex].getAttribute('data-amount')
                      const name = e.target[e.target.selectedIndex].getAttribute('data-name')
                      setFieldValue('donation_amount', amount || '')
                      setFieldValue('product_name', name || '')
                      if (!e.target.value) {
                        setFieldValue('donation_amount_info', '')
                      }
                      handleChange(e)
                    }}
                  >
                    <option value="">Select donation type</option>
                    {getDetails.map((item) => (
                      <option
                        key={item.donation_type_id}
                        value={item.donation_type_id}
                        data-amount={item.amount}
                        data-name={item.name}
                      >
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="donation_amount">
                    दान राशी / Amount
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={values.donation_amount}
                    onChange={handleChange}
                    id="donation_amount"
                    name="donation_amount"
                  />
                </div>
              </div>
              {values.product_id ? (
                <div className="mb-3">
                  <label className="form-label" htmlFor="donation_amount_info">
                    दान राशि संबंधी जानकारी{' '}
                    <span className="text-muted">(वैकल्पिक / Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="donation_amount_info"
                    name="donation_amount_info"
                    value={values.donation_amount_info}
                    onChange={handleChange}
                  />
                </div>
              ) : null}

              <div className="donation-actions">
                <button
                  className="btn donation-submit-btn"
                  type="submit"
                  disabled={payingId === 'checkout'}
                >
                  {payingId === 'checkout' ? 'Opening payment…' : 'Donate'}
                </button>
              </div>
            </Form>
          </div>

          {loggedIn ? (
            <div className="donation-card donation-pending-card">
              <div className="donation-card-header">
                <h2 className="donation-card-title">Pending donations</h2>
                <p className="donation-card-subtitle">
                  Complete payment for unpaid donations below. Paid items leave this list
                  automatically.
                </p>
              </div>

              {payMessage ? <p className="donation-pay-message">{payMessage}</p> : null}

              {pendingLoading ? (
                <p className="donation-empty">Loading pending donations…</p>
              ) : pendingDonations.length === 0 ? (
                <p className="donation-empty">You have no pending / unpaid donations.</p>
              ) : (
                <div className="table-responsive donation-pending-table-wrap">
                  <table className="table donation-pending-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Order / Receipt</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDonations.map((row) => {
                        const rowKey = row.payment_detail_id || row.payment_id || row.order_id
                        const busy =
                          payingId != null &&
                          String(payingId) === String(row.payment_detail_id || row.payment_id)
                        return (
                          <tr key={rowKey}>
                            <td>{row.created_at || '—'}</td>
                            <td>{row.type_name || row.donation_type_name || '—'}</td>
                            <td>{formatAmount(row.payment_amount)}</td>
                            <td>{row.order_id || row.payment_id || '—'}</td>
                            <td>
                              <span className="donation-status-pending">
                                {row.payment_status || 'Pending'}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm donation-pay-btn"
                                disabled={busy}
                                onClick={() => payPendingDonation(row)}
                              >
                                {busy ? 'Opening…' : 'Make payment'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="donation-history-link">
                <Link to="/DonationHistory">View full donation history</Link>
              </p>
            </div>
          ) : null}
        </div>
      </section>
      <Footer />
    </>
  )
}

export default Donation
